// src/routes/dashboard/components/MyTasksCard.tsx
/** Le task assegnate all'utente corrente: conteggi più le prime per scadenza. */
import React from 'react';
import { Badge, Button, Card, Group, Stack, Text, Title, Box } from '@mantine/core';
import { IconChecklist, IconLayoutKanban } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { MyTasks } from '../../../types/api';
import { TYPE_META } from '../../../components/analytics/analytics-meta';
import { formatDaysToDue } from '../dashboard-meta';

interface MyTasksCardProps {
  myTasks: MyTasks;
}

const PRIORITY_COLOR: Record<string, string> = {
  Low: 'gray',
  Medium: 'blue',
  High: 'orange',
  Critical: 'red',
};

export function MyTasksCard({ myTasks }: MyTasksCardProps) {
  const navigate = useNavigate();

  const openRequest = (requestId: number) => {
    navigate({ to: `/requests/${requestId}`, search: { from: '/' } } as any);
  };

  return (
    <Card withBorder p="md" h="100%">
      <Group justify="space-between" mb="sm">
        <Title order={4}>Le mie task</Title>
        <Group gap={6}>
          <Badge color="blue" variant="light" size="sm">
            {myTasks.openCount} aperte
          </Badge>
          {myTasks.overdueCount > 0 && (
            <Badge color="red" variant="light" size="sm">
              {myTasks.overdueCount} in ritardo
            </Badge>
          )}
        </Group>
      </Group>

      {myTasks.items.length === 0 ? (
        <Group c="dimmed" py="lg" justify="center">
          <IconChecklist size={18} />
          <Text size="sm">Non hai task aperte assegnate</Text>
        </Group>
      ) : (
        <Stack gap="xs">
          {myTasks.items.map((task) => {
            const dueLabel = formatDaysToDue(task.daysToDue);
            return (
              <Group
                key={task.id}
                justify="space-between"
                wrap="nowrap"
                p="xs"
                style={{ cursor: 'pointer', borderRadius: 6 }}
                onClick={() => openRequest(task.requestId)}
              >
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                  <Badge color={PRIORITY_COLOR[task.priority] ?? 'gray'} variant="dot" size="sm">
                    {task.priority}
                  </Badge>
                  <Box style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500} truncate>
                      {task.title}
                    </Text>
                    <Text size="xs" c="dimmed" truncate>
                      {TYPE_META[task.requestType]?.label} · {task.requestProgressiveNumberFormatted}
                      {task.isInternalDueDate ? ' · scadenza interna' : ''}
                    </Text>
                  </Box>
                </Group>
                {dueLabel && (
                  <Text size="xs" c={task.isOverdue ? 'red' : 'orange'} fw={600} style={{ whiteSpace: 'nowrap' }}>
                    {dueLabel}
                  </Text>
                )}
              </Group>
            );
          })}
        </Stack>
      )}

      <Button
        mt="md"
        variant="subtle"
        size="xs"
        leftSection={<IconLayoutKanban size={14} />}
        onClick={() => navigate({ to: '/board' })}
        fullWidth
      >
        Apri Kanban
      </Button>
    </Card>
  );
}
